import { useEffect, useMemo, useRef } from "react"
import { Icon } from "@/components/Icon"
import { parseAlignedAllegations } from "@/features/oio/lib/allegations"
import {
  VERDICT_PILL,
  findingSummaryLine,
  formatINR,
  mapDeterminationStatus,
  parseFindingsSummary,
} from "@/features/oio/lib/findings"

/**
 * "From the record so far" — a progressive feed of REAL case content that
 * fills as node boundaries flush agent state during generation:
 *
 *   1. aligned_allegations_json (alignment boundary) → the main ISSUES under
 *      adjudication, with each head's amount in dispute where the record
 *      states one.
 *   2. reply_status === "not_received" → a quiet ex-parte chip.
 *   3. findings_summary_json (speaking_order boundary, MID-RUN — well before
 *      the order compiles) → the provisional STANCE per head.
 *
 * Pure state derivation: no polling, no requests — everything comes from the
 * already-streamed OIO agent state passed in as props.
 */
interface LiveFindingsFeedProps {
  alignedAllegationsJson?: string[] | null
  findingsSummaryJson?: string | null
  replyStatus?: string | null
}

function truncate(text: string, max: number): string {
  const t = (text ?? "").trim().replace(/\s+/g, " ")
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

export function LiveFindingsFeed({
  alignedAllegationsJson,
  findingsSummaryJson,
  replyStatus,
}: LiveFindingsFeedProps) {
  const issues = useMemo(
    () => parseAlignedAllegations(alignedAllegationsJson),
    [alignedAllegationsJson],
  )
  const findings = useMemo(() => parseFindingsSummary(findingsSummaryJson), [findingsSummaryJson])

  // Auto-reveal the latest section: when a new section lands (issues → stance),
  // scroll the feed to its end so the newest content is visible without the
  // officer chasing it.
  const scrollRef = useRef<HTMLDivElement>(null)
  const sectionCount = (issues.length > 0 ? 1 : 0) + (findings.length > 0 ? 1 : 0)
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [sectionCount])

  // Pre-alignment there is nothing real to show — render nothing.
  if (issues.length === 0 && findings.length === 0) return null

  return (
    <div className="feed" data-testid="live-findings-feed" aria-live="polite">
      <div className="feed__head">
        <Icon name="scale" size={15} />
        <h3>From the record so far</h3>
      </div>
      <div className="feed__scroll" ref={scrollRef}>
        {issues.length > 0 && (
          <section data-testid="feed-issues">
            <div className="feed__label">Issues under adjudication</div>
            {issues.map((a) => (
              <div className="feed__row" key={a.id}>
                <span className="feed__id">{a.id}</span>
                <span className="feed__desc">{truncate(a.title, 120)}</span>
                <span
                  className="feed__amt"
                  title={
                    a.amount != null
                      ? "Amount in dispute, as stated in the notice"
                      : "The aligned record does not state this head's amount — the analysis will quantify it"
                  }
                >
                  {a.amount != null ? formatINR(a.amount) : "—"}
                </span>
              </div>
            ))}
          </section>
        )}

        {replyStatus === "not_received" && (
          <div className="feed__note">
            <span className="chip" data-testid="feed-exparte-chip">
              No reply filed — proceeding ex parte on the record.
            </span>
          </div>
        )}

        {findings.length > 0 && (
          <section data-testid="feed-stance">
            <div className="feed__label">
              Where the deliberation stands
              <span className="feed__provisional">subject to final compilation</span>
            </div>
            {findings.map((f) => {
              const status = mapDeterminationStatus(f.determination_status)
              const pill = VERDICT_PILL[status]
              const line = findingSummaryLine(f)
              const demand = Number(f.demand_computation?.total_demand) || 0
              return (
                <div className="feed__row" key={f.allegation_id}>
                  <span className="feed__id">{f.allegation_id}</span>
                  <span className="feed__desc">
                    <span className={"pill pill--" + pill.cls} data-testid={`stance-pill-${f.allegation_id}`}>
                      {pill.label}
                    </span>
                    {line && <span className="feed__basis"> {truncate(line, 140)}</span>}
                  </span>
                  {status !== "dropped" && demand > 0 && (
                    <span className="feed__amt" title="Demand surviving on this head">
                      {formatINR(demand)}
                    </span>
                  )}
                </div>
              )
            })}
          </section>
        )}
      </div>
    </div>
  )
}
